import { wireChannelName } from "@thalamos/common/realtime";
const DEFAULT_DEBOUNCE_MS = 500;
export class SubscriptionRegistry {
    channels = new Map();
    eventName;
    debounceMs;
    onListenerError;
    transport;
    prefix;
    stopListeningForReconnect;
    constructor(options) {
        this.eventName = options.eventName;
        this.debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
        this.onListenerError = options.onListenerError ?? (() => undefined);
    }
    /**
     * Point the registry at a transport, moving every live subscription onto it.
     *
     * Called with `undefined` when there is no connection — on sign-out, or
     * before one is established. Listeners are kept either way, so a channel
     * somebody is still watching is re-subscribed rather than lost.
     */
    retarget(transport, prefix) {
        for (const [channel, entry] of this.channels) {
            this.detach(channel, entry);
        }
        this.stopListeningForReconnect?.();
        this.transport = transport;
        this.prefix = prefix;
        this.stopListeningForReconnect = transport?.onReconnect(() => {
            this.reloadEverything();
        });
        for (const [channel, entry] of this.channels) {
            this.attach(channel, entry);
        }
    }
    /**
     * Watch `channels`, calling `listener` when any of them changes.
     *
     * Returns the function that stops watching. Calling it is what releases the
     * underlying subscriptions, and only once nobody else holds them.
     */
    listen(channels, listener) {
        for (const channel of channels) {
            this.entryFor(channel).listeners.add(listener);
        }
        return () => {
            for (const channel of channels) {
                this.drop(channel, listener);
            }
        };
    }
    /**
     * A handle for one listener whose set of channels changes over time.
     *
     * `to` reconciles by difference rather than by replacing the set, so a
     * channel present before and after is never released in between — a listener
     * that widens its set would otherwise lose the channels it already held for
     * as long as it takes to re-take them, missing anything announced in between.
     */
    watch(listener) {
        let watched = new Set();
        const to = (channels) => {
            const wanted = new Set(channels);
            // Added before removed, so a channel in both sets never reaches zero
            // listeners and is left subscribed throughout.
            for (const channel of wanted) {
                if (!watched.has(channel)) {
                    this.entryFor(channel).listeners.add(listener);
                }
            }
            for (const channel of watched) {
                if (!wanted.has(channel)) {
                    this.drop(channel, listener);
                }
            }
            watched = wanted;
        };
        return {
            to,
            stop: () => {
                to([]);
            },
        };
    }
    /** The channels currently subscribed, for tests and diagnostics. */
    get watching() {
        return [...this.channels.keys()];
    }
    drop(channel, listener) {
        const entry = this.channels.get(channel);
        if (entry === undefined) {
            return;
        }
        entry.listeners.delete(listener);
        if (entry.listeners.size === 0) {
            this.release(channel, entry);
        }
    }
    entryFor(channel) {
        const existing = this.channels.get(channel);
        if (existing !== undefined) {
            return existing;
        }
        const entry = {
            listeners: new Set(),
            subscription: undefined,
            timer: undefined,
        };
        this.channels.set(channel, entry);
        this.attach(channel, entry);
        return entry;
    }
    /** See `wireChannelName` for why a namespace goes after the kind marker. */
    wireName(channel) {
        return wireChannelName(this.prefix, channel);
    }
    attach(channel, entry) {
        if (this.transport === undefined || entry.subscription !== undefined) {
            return;
        }
        const subscription = this.transport.subscribe(this.wireName(channel));
        // One binding per channel rather than one per listener: the registry fans
        // out, so a component mounting does not add work to the transport.
        subscription.bind(this.eventName, () => {
            this.scheduleReload(entry);
        });
        entry.subscription = subscription;
    }
    detach(channel, entry) {
        if (entry.subscription === undefined) {
            return;
        }
        entry.subscription.unbindAll();
        this.transport?.unsubscribe(this.wireName(channel));
        entry.subscription = undefined;
    }
    release(channel, entry) {
        this.detach(channel, entry);
        if (entry.timer !== undefined) {
            clearTimeout(entry.timer);
        }
        this.channels.delete(channel);
    }
    scheduleReload(entry) {
        if (entry.timer !== undefined) {
            clearTimeout(entry.timer);
        }
        entry.timer = setTimeout(() => {
            entry.timer = undefined;
            this.notify(entry.listeners);
        }, this.debounceMs);
    }
    /**
     * Tell every listener to re-read, after a connection came back.
     *
     * Events published while the connection was down were never delivered and
     * cannot be recovered, so the only correct response to reconnecting is to
     * re-read everything being watched. Without this a dropped connection leaves
     * a page showing stale data indefinitely, and looking exactly like a page
     * showing current data.
     */
    reloadEverything() {
        for (const entry of this.channels.values()) {
            this.scheduleReload(entry);
        }
    }
    notify(listeners) {
        // Copied before iterating: a listener may stop watching as it runs, which
        // would otherwise mutate the set mid-iteration.
        for (const listener of [...listeners]) {
            try {
                listener();
            }
            catch (error) {
                this.onListenerError(error);
            }
        }
    }
}
//# sourceMappingURL=SubscriptionRegistry.js.map